import React, { createContext, useContext, useEffect } from 'react'
import { useLocation, useMatches } from 'react-router-dom'
import { atom, useRecoilState } from 'recoil'

import { useSitemapItems } from '../../../state/sitemap'
import { HistoryStack, UIMatchWithSearch } from './history-stack'

interface HistoryContext {
  historyStack: HistoryStack
  updateCurrentSearch: (newSearch: string) => void
}

const INITIAL_STATE: HistoryContext = {
  historyStack: new HistoryStack([]),
  updateCurrentSearch: () => {},
}

const historyContext = createContext<HistoryContext>(INITIAL_STATE)
export const useHistoryContext = () => useContext(historyContext)

interface Props {
  children: React.ReactNode
}

const $historyStack = atom<HistoryStack>({
  key: 'HistoryStack',
  default: new HistoryStack([]),
})

/**
 * `HistoryProvider` dedicated provider for tracking history of navigations
 * keeps track of all previous visited urls in a stack
 */
export function HistoryProvider(props: Props) {
  const [stack, setStack] = useRecoilState($historyStack)
  const matches = useMatches()
  const location = useLocation()
  const sitemapItems = useSitemapItems()

  useEffect(() => {
    const newMatches: UIMatchWithSearch[] = []
    matches
      .filter((m) => m.pathname !== '/')
      .forEach((m) => {
        const match: any = {
          ...m,
          search: location.search,
          isCustom: !m.params['entityName'] && location.pathname.includes('CUSTOM'),
        }

        // If the match has a sitemapId, we need to extract moduleName and entityName from it
        // so that the routing with sitemapIds is transparent to the rest of the application
        if (match.params['sitemapId']) {
          match.params = { ...match.params }
          const sitemapItem = sitemapItems.find((item) => item.id === match.params['sitemapId'])
          if (sitemapItem) {
            match.params['moduleName'] = sitemapItem.entries
              .map((entry) => `${entry.entity}:${entry.viewName}`)
              .join(',')
            match.params['entityName'] = sitemapItem.entries[0].entity
          }
        }
        newMatches.push(match)
      })
    const newStack = [...stack, ...newMatches] as UIMatchWithSearch[]

    setStack(new HistoryStack(newStack))
  }, [location])

  /**
   * update the current history entry with updated search params
   * this is used to updated the filters in the current view
   * as they are updated in the url using the history replaceState
   */
  const updateCurrentSearch = (newSearch: string) => {
    const current = stack.current()
    if (current) {
      const cloned = { ...current }
      cloned.search = newSearch
      const newStack = [...stack, cloned] as UIMatchWithSearch[]
      setStack(new HistoryStack(newStack))
    }
  }

  return (
    <historyContext.Provider value={{ historyStack: stack, updateCurrentSearch }}>
      {props.children}
    </historyContext.Provider>
  )
}
